import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { GlobalStyles } from '@mui/material';
import { theme, globalStyles } from './theme';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';

// Import components
import Navbar from './components/Navbar';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import AdminPanel from './components/AdminPanel';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ActivityProvider } from './contexts/ActivityContext';
import { CustomThemeProvider, useThemeMode } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { SettingsButton, SettingsDialog } from './components/Settings';

function AppContent() {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const { theme: themeMode, darkMode } = useThemeMode();
  const [currentView, setCurrentView] = useState('dashboard');
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  // Security check: redirect non-admins away from admin view
  React.useEffect(() => {
    if (currentView === 'admin' && !isAdmin) {
      setCurrentView('dashboard');
    }
  }, [currentView, isAdmin]);

  if (loading) {
    // Apply dark/light mode to theme
    const currentTheme = {
      ...theme,
      palette: {
        ...theme.palette,
        mode: darkMode ? 'dark' : 'light',
      },
    };

    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: currentTheme.palette.background.default,
        color: currentTheme.palette.text.primary
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <NotificationProvider>
      <SettingsProvider>
        <Router>
          <div>
            <Navbar currentView={currentView} setCurrentView={setCurrentView} />
            <Container>
              {currentView === 'dashboard' && <Dashboard />}
              {currentView === 'admin' && isAdmin && <AdminPanel />}
            </Container>
            <SettingsButton onClick={() => setSettingsOpen(true)} />
            <SettingsDialog 
              open={settingsOpen} 
              onClose={() => setSettingsOpen(false)} 
            />
          </div>
        </Router>
      </SettingsProvider>
    </NotificationProvider>
  );
}

function App() {
  return (
    <CustomThemeProvider>
      <AppWrapper />
    </CustomThemeProvider>
  );
}

function AppWrapper() {
  const { theme: themeMode, darkMode } = useThemeMode();
  
  // Apply dark/light mode to theme
  const currentTheme = {
    ...theme,
    palette: {
      ...theme.palette,
      mode: darkMode ? 'dark' : 'light',
    },
  };
  
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={currentTheme}>
        <CssBaseline />
        <GlobalStyles styles={globalStyles} />
        <ActivityProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ActivityProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
